import { useState, useEffect, useCallback } from 'react';
import { adminGetDrafts, adminDeleteDraft, adminConfirmDraft } from '../../utils/api';
import { formatDate, PAYMENT_METHODS } from '../../utils/helpers';
import toast from 'react-hot-toast';
import { FiTrash2, FiCheck, FiX, FiSearch } from 'react-icons/fi';
import styles from './AdminTable.module.css';

const PM_LABELS = Object.fromEntries(
  PAYMENT_METHODS.map(m => [m.id, { label: m.label, icon: m.icon, color: m.color }])
);

export default function AdminDrafts() {
  const [drafts, setDrafts]         = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmForm, setConfirmForm]   = useState({ paymentMethod: 'cod', paymentTransactionId: '', note: '' });
  const [processing, setProcessing]     = useState(false);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminGetDrafts({ page, limit: 15, search });
      setDrafts(data.drafts);
      setTotal(data.total);
    } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete draft from "${name}"? This cannot be undone.`)) return;
    try {
      await adminDeleteDraft(id);
      toast.success('Draft deleted');
      fetchDrafts();
    } catch { toast.error('Failed to delete'); }
  };

  const openConfirm = (draft) => {
    setConfirmModal(draft);
    setConfirmForm({
      paymentMethod:        draft.paymentMethod || 'cod',
      paymentTransactionId: draft.paymentTransactionId || '',
      note:                 draft.note || '',
    });
  };

  const handleConfirm = async () => {
    if (!confirmModal) return;
    setProcessing(true);
    try {
      await adminConfirmDraft(confirmModal._id, confirmForm);
      toast.success('✅ Draft confirmed as real order!');
      setConfirmModal(null);
      fetchDrafts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to confirm');
    } finally { setProcessing(false); }
  };

  const pages = Math.ceil(total / 15);
  const manualMethods = ['bkash', 'nagad', 'rocket'];

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Draft Orders</h2>
          <p className={styles.pageSubtitle}>
            {total} abandoned checkouts — customers who filled the form but did not complete payment
          </p>
        </div>
      </div>

      {/* Info banner */}
      <div style={{ background: '#fef3c7', border: '1.5px solid #fcd34d', borderRadius: 10, padding: '12px 16px', fontSize: '.83rem', color: '#92400e' }}>
        💡 These are <strong>draft orders</strong> saved automatically when a customer starts filling the checkout form.
        You can call the customer, confirm payment manually and convert the draft into a real order.
      </div>

      {/* Search */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch size={15} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search by customer name or phone..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && <button className={styles.clearSearch} onClick={() => setSearch('')}><FiX size={13} /></button>}
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        {loading ? (
          <div className="spinner-center"><div className="spinner" /></div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Location</th>
                  <th>Saved At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {drafts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={styles.emptyRow}>
                      🎉 No abandoned checkouts! All customers are completing their orders.
                    </td>
                  </tr>
                ) : drafts.map(d => {
                  const pm = PM_LABELS[d.paymentMethod] || { label: d.paymentMethod, icon: '💳', color: '#6b7280' };
                  return (
                    <tr key={d._id}>
                      <td>
                        <div className={styles.productCell}>
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                            background: 'var(--primary-light)', color: 'var(--primary)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 800, fontSize: '.875rem',
                          }}>
                            {d.shippingAddress?.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className={styles.productName}>{d.shippingAddress?.name || 'Unknown'}</p>
                            <p className={styles.productSub}>{d.shippingAddress?.phone || '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: '.82rem' }}>{d.items?.length || 0} item(s)</td>
                      <td><strong style={{ fontSize: '.875rem' }}>৳{d.totalPrice?.toLocaleString()}</strong></td>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: pm.color + '18', color: pm.color,
                          border: `1px solid ${pm.color}40`,
                          padding: '3px 8px', borderRadius: 6,
                          fontSize: '.72rem', fontWeight: 700,
                        }}>
                          {pm.icon} {pm.label}
                        </span>
                      </td>
                      <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                        {d.shippingAddress?.district && `${d.shippingAddress.district}, `}
                        {d.shippingAddress?.division || '—'}
                      </td>
                      <td style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
                        {formatDate(d.draftSavedAt || d.createdAt)}
                      </td>
                      <td>
                        <div className={styles.actionBtns}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => openConfirm(d)}
                            title="Confirm as real order"
                          >
                            <FiCheck size={13} /> Confirm
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: 'var(--error)' }}
                            onClick={() => handleDelete(d._id, d.shippingAddress?.name || 'Unknown')}
                            title="Delete draft"
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="pagination">
            {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
              <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>

      {/* Confirm Draft Modal */}
      {confirmModal && (
        <div className={styles.modalOverlay} onClick={() => setConfirmModal(null)}>
          <div className={styles.modal} style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>✅ Confirm Draft Order</h3>
              <button className={styles.modalClose} onClick={() => setConfirmModal(null)}><FiX size={20} /></button>
            </div>

            <div className={styles.modalForm}>
              {/* Customer info */}
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px', marginBottom: '1rem' }}>
                <p style={{ fontWeight: 700, fontSize: '.875rem', marginBottom: 4 }}>👤 Customer</p>
                <p style={{ fontSize: '.84rem' }}>{confirmModal.shippingAddress?.name} — {confirmModal.shippingAddress?.phone}</p>
                <p style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>
                  {confirmModal.shippingAddress?.address}, {confirmModal.shippingAddress?.district}, {confirmModal.shippingAddress?.division}
                </p>
              </div>

              {/* Items */}
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontWeight: 700, fontSize: '.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Order Items</p>
                {confirmModal.items?.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                    <img src={item.image} alt={item.name} style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} />
                    <span style={{ flex: 1, fontSize: '.82rem', fontWeight: 500 }}>{item.name}</span>
                    <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>×{item.quantity}</span>
                    <strong style={{ fontSize: '.82rem' }}>৳{(item.price * item.quantity).toLocaleString()}</strong>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, marginTop: 8 }}>
                  <span>Total</span>
                  <span style={{ color: 'var(--accent)' }}>৳{confirmModal.totalPrice?.toLocaleString()}</span>
                </div>
              </div>

              {/* Edit payment method */}
              <div className="form-group" style={{ marginBottom: '.875rem' }}>
                <label className="form-label">Payment Method *</label>
                <select
                  className="form-input form-select"
                  value={confirmForm.paymentMethod}
                  onChange={e => setConfirmForm(f => ({ ...f, paymentMethod: e.target.value }))}
                >
                  {PAYMENT_METHODS.map(m => (
                    <option key={m.id} value={m.id}>{m.icon} {m.label}</option>
                  ))}
                </select>
              </div>

              {/* Transaction ID for manual methods */}
              {manualMethods.includes(confirmForm.paymentMethod) && (
                <div className="form-group" style={{ marginBottom: '.875rem' }}>
                  <label className="form-label">
                    Transaction ID
                    <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginLeft: 6 }}>
                      (enter if customer has paid)
                    </span>
                  </label>
                  <input
                    className="form-input"
                    placeholder="e.g. TXN1234567890"
                    value={confirmForm.paymentTransactionId}
                    onChange={e => setConfirmForm(f => ({ ...f, paymentTransactionId: e.target.value.toUpperCase() }))}
                  />
                </div>
              )}

              {/* Note */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Internal Note (optional)</label>
                <input
                  className="form-input"
                  placeholder="e.g. Called customer, confirmed bKash payment"
                  value={confirmForm.note}
                  onChange={e => setConfirmForm(f => ({ ...f, note: e.target.value }))}
                />
              </div>

              <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', fontSize: '.78rem', color: '#92400e' }}>
                ⚠️ This will convert the draft into a real order, reduce product stock, and assign an order number.
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className="btn btn-ghost" onClick={() => setConfirmModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirm} disabled={processing}>
                {processing ? <><div className="spinner spinner-sm" /> Processing…</> : '✅ Confirm Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
